'use client';

import { saveServerThemePreference } from '@/actions/theme';
import { DEFAULT_THEME } from '@/lib/consts';
import { getUserThemePreference, Theme } from '@/lib/util/theme';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';

export type ThemeContextType = {
  theme: Theme;
  setTheme: (t: Theme) => void;
  pending: boolean;
};

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children, savedTheme }: PropsWithChildren<{ savedTheme?: Theme }>) {
  const [pending, setPending] = useState(!savedTheme);

  const setTheme = async (t: Theme) => {
    setPending(true);
    await saveServerThemePreference(t);
    setPending(false);
  };

  useEffect(() => {
    // Theme not saved yet, get user preference and set theme on server. The changed cookie will
    // cause a page reload which will change the theme
    if (!savedTheme) {
      const userPrefernce = getUserThemePreference();
      setTheme(userPrefernce);
    }
  }, [savedTheme]);

  return (
    <ThemeContext.Provider value={{ theme: savedTheme ?? DEFAULT_THEME, setTheme, pending }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');

  return context;
}
